import React, { useState, useCallback, useContext } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Tooltip,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { format } from "date-fns"; // For formatting dates
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import { useNavigate } from "react-router-dom";

import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import axios from "axios";
import { toast } from "react-toastify";
import CustomCircularP from "../../widgets/CustomCircularP";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import CursorNavButtons from "../../widgets/CursorNavButtons";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import PageTitle from "../../widgets/PageTitle";
import DebouncedSearchBox from "./DebounceSearchBox";
import EditIcon from "@mui/icons-material/Edit";
import { FaPlus } from "react-icons/fa";
import { HiOutlineArrowUpTray } from "react-icons/hi2";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";
import { UserContext } from "../../context/UserProvider";
/**
 * Helper to map WABA template status → Chip color
 */
const statusColor = {
  APPROVED: "success",
  PENDING: "warning",
  IN_APPEAL: "warning",
  PAUSED: "warning",
  DISABLED: "error",
  REJECTED: "error",
};

const MyTemplates = () => {
  const { user: userData, loading: profileLoading } = useContext(UserContext);
  const [navCode, setNavCode] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const {
    data,
    isLoading,
    isError,
    error: errorD,
    refetch,
  } = useApiEp(
    `/api/get-templates-directly?navCode=${navCode}&cid=${selectedAccount}`
  );
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [toDeleteId, setToDeleteId] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const accounts = userData?.accounts || [];

  const handleExportExcel = useCallback(async () => {
    try {
      setExporting(true);
      const response = await axios.get(
        `${API_ENDPOINT}/api/get-all-templates`,
        getHeader()
      );

      // Prepare the data for export
      const templates = response.data; // Assuming this is your template data
      const exportData = templates.map((template) => ({
        "Template ID": template.id,
        Name: template.name,
        Language: template.language,
        Status: template.status,
        Category: template.category,
        "Customer ID": template.customerId,
        "WABA Account ID": template.wabaAccountId,
        "Body Text":
          template.components.find((c) => c.type === "BODY")?.text || "",
        "Footer Text":
          template.components.find((c) => c.type === "FOOTER")?.text || "",
        Buttons:
          template.components
            .find((c) => c.type === "BUTTONS")
            ?.buttons.map((b) => b.text)
            .join(", ") || "",
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Templates");
      XLSX.writeFile(wb, "whatsapp_templates_export.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setExporting(false);
    }
  }, []);
  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(errorD)}</Alert>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Global overlays */}
      <CustomCircularP show={deleting} />
      <SimpleYesNoDialog
        show={showDelete}
        setShow={setShowDelete}
        title={"Please confirm!"}
        message={"Are you sure you want to delete this item?"}
        onYes={async () => {
          try {
            setDeleting(true);
            setShowDelete(false);
            const response = await axios.delete(
              `${API_ENDPOINT}/api/delete-template/${toDeleteId.id}/${toDeleteId.name}`,
              getHeader()
            );
            refetch();
            toast.success(response.data.message);
          } catch (error) {
            toastError(error);
          } finally {
            setDeleting(false);
          }
        }}
      />

      <TitleActionsContainer>
        <PageTitle>My Templates</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
        >
          <DebouncedSearchBox
            apiUrl={`${API_ENDPOINT}/api/search-template-by-name`}
            placeholder="Search Templates"
          />
          {accounts.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 192 }}>
              <InputLabel>Filter by Accounts</InputLabel>
              <Select
                value={selectedAccount}
                label="Filter by Accounts"
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                <MenuItem value="">Default Account</MenuItem>
                {accounts.map((acc) => (
                  <MenuItem key={acc} value={acc}>
                    {acc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <GlassButtonLoading
            startIcon={<HiOutlineArrowUpTray />}
            variant="contained"
            loading={exporting}
            onClick={handleExportExcel}
          >
            Export
          </GlassButtonLoading>
          <GlassButton
            onClick={() => navigate(`/home/create-template`)}
            variant="outlined"
            startIcon={<FaPlus />}
          >
            Add Template
          </GlassButton>
        </Stack>
      </TitleActionsContainer>

      {/* State banners */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Templates Table */}
      {!loading && !error && (
        <Paper elevation={1}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created On</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data ? (
                  data?.data?.map((template) => {
                    const color = statusColor[template.status] || "default";
                    const chip = (
                      <Chip
                        size="small"
                        label={template.status}
                        color={color}
                        variant={color === "default" ? "outlined" : "filled"}
                      />
                    );

                    return (
                      <TableRow hover key={template._id}>
                        <TableCell>{template.name}</TableCell>
                        <TableCell>{template.id}</TableCell>
                        <TableCell>{template.category || "-"}</TableCell>
                        <TableCell>{template.language}</TableCell>
                        <TableCell>
                          {template.status === "REJECTED" &&
                          template.rejectedReason ? (
                            <Tooltip title={template.rejectedReason} arrow>
                              {chip}
                            </Tooltip>
                          ) : (
                            chip
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(template.last_updated_time), "PPp")}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="View Campaign" arrow>
                            <IconButton
                              onClick={() =>
                                navigate(
                                  `/home/${template.id}/template-tracker`
                                )
                              }
                              color="primary"
                              size="small"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                  color: "primary.dark",
                                },
                              }}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Template" arrow>
                            <IconButton
                              onClick={() =>
                                navigate(
                                  `/home/edit-template?tid=${template.id}`
                                )
                              }
                              color="secondary"
                              size="small"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "secondary.light",
                                  color: "secondary.dark",
                                },
                                mx: 1,
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Template" arrow>
                            <IconButton
                              onClick={() => {
                                setToDeleteId(template);
                                setShowDelete(true);
                              }}
                              color="error"
                              size="small"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "error.light",
                                  color: "error.dark",
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No templates found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box my={1} py={1}>
            <CursorNavButtons
              cursors={
                data?.paging?.cursors ? data?.paging?.cursors : data?.cursors
              }
              onNav={(newV) => {
                setNavCode(newV);
              }}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MyTemplates;
